import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FiMail, FiLock, FiEye, FiEyeOff, FiUser, FiPhone, FiBriefcase, FiArrowRight, FiArrowLeft, FiAlertCircle, FiCheck } from 'react-icons/fi';
import './auth.css';

const getPasswordStrength = (pwd) => {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { level: 1, label: 'Faible', color: '#ef4444', width: '25%' };
  if (score === 2) return { level: 2, label: 'Moyen', color: '#f59e0b', width: '50%' };
  if (score === 3) return { level: 3, label: 'Bon', color: '#3b82f6', width: '75%' };
  return { level: 4, label: 'Excellent', color: '#10b981', width: '100%' };
};

const RegisterPage = () => {
  const [step, setStep] = useState(1); // 1 = infos perso, 2 = agence + mdp
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    agency_name: '', password: '', confirm_password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleNextStep = (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setError('');
    setStep(2);
  };

  const handlePreviousStep = (e) => {
    e.preventDefault();
    setStep(1);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (form.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (!acceptTerms) {
      setError('Veuillez accepter les conditions d\'utilisation');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { confirm_password, ...payload } = form;
      await register(payload);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const strength = form.password ? getPasswordStrength(form.password) : null;

  return (
    <div className="auth-wrapper">
      {/* Panneau gauche */}
      <div className="auth-left">
        <div className="auth-left-content">
          <div className="auth-brand">
            <div className="auth-brand-icon">🏢</div>
            <div className="auth-brand-name">Gestion<span>Immo</span></div>
          </div>
          <h2>Démarrez gratuitement dès aujourd'hui</h2>
          <p>Rejoignez plus de 500 agences immobilières qui font confiance à GestionImmo pour gérer leur activité.</p>
          <ul className="auth-features-list">
            <li><div className="auth-feature-icon"><FiCheck size={16} /></div>Essai gratuit 30 jours, sans carte bancaire</li>
            <li><div className="auth-feature-icon"><FiCheck size={16} /></div>Configuration en moins de 5 minutes</li>
            <li><div className="auth-feature-icon"><FiCheck size={16} /></div>Support dédié inclus</li>
            <li><div className="auth-feature-icon"><FiCheck size={16} /></div>Données sécurisées et hébergées en Afrique</li>
          </ul>
        </div>
      </div>

      {/* Panneau droit */}
      <div className="auth-right">
        <div className="auth-right-header">
          <h1>{step === 1 ? 'Créer un compte' : 'Votre agence'}</h1>
          <p>{step === 1 ? 'Étape 1 sur 2 — Vos informations personnelles' : 'Étape 2 sur 2 — Configurez votre agence'}</p>
        </div>

        {/* Indicateur d'étapes */}
        <div className="auth-step-indicator">
          <div className={`auth-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'done' : ''}`} />
          <div className={`auth-step ${step >= 2 ? 'active' : ''}`} />
        </div>

        {error && (
          <div className="auth-alert-error">
            <FiAlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Étape 1 */}
        {step === 1 && (
          <form className="auth-form" onSubmit={handleNextStep}>
            <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label>Prénom *</label>
                <div className="auth-input-wrapper">
                  <FiUser size={16} className="auth-input-icon" />
                  <input 
                    type="text" 
                    name="first_name" 
                    className="form-control" 
                    value={form.first_name} 
                    onChange={handleChange} 
                    placeholder="Amadou" 
                    required 
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Nom *</label>
                <div className="auth-input-wrapper">
                  <FiUser size={16} className="auth-input-icon" />
                  <input 
                    type="text" 
                    name="last_name" 
                    className="form-control" 
                    value={form.last_name} 
                    onChange={handleChange} 
                    placeholder="Diallo" 
                    required 
                  />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>Adresse email *</label>
              <div className="auth-input-wrapper">
                <FiMail size={16} className="auth-input-icon" />
                <input 
                  type="email" 
                  name="email" 
                  className="form-control" 
                  value={form.email} 
                  onChange={handleChange} 
                  placeholder="amadou@agence.ml" 
                  required 
                />
              </div>
            </div>
            <div className="form-group">
              <label>Téléphone</label>
              <div className="auth-input-wrapper">
                <FiPhone size={16} className="auth-input-icon" />
                <input 
                  type="tel" 
                  name="phone" 
                  className="form-control" 
                  value={form.phone} 
                  onChange={handleChange} 
                  placeholder="+223 XX XX XX XX" 
                />
              </div>
            </div>
            <button 
              type="submit" 
              className="auth-btn auth-btn-primary auth-btn-lg" 
              style={{ width: '100%', marginTop: 16 }}
            >
              Continuer <FiArrowRight size={18} style={{ marginLeft: 8 }} />
            </button>
          </form>
        )}

        {/* Étape 2 */}
        {step === 2 && (
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nom de votre agence *</label>
              <div className="auth-input-wrapper">
                <FiBriefcase size={16} className="auth-input-icon" />
                <input 
                  type="text" 
                  name="agency_name" 
                  className="form-control" 
                  value={form.agency_name} 
                  onChange={handleChange} 
                  placeholder="Agence Immobilière Diallo" 
                  required 
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Mot de passe *</label>
              <div className="auth-input-wrapper">
                <FiLock size={16} className="auth-input-icon" />
                <input 
                  type={showPassword ? "text" : "password"} 
                  name="password" 
                  className="form-control" 
                  value={form.password} 
                  onChange={handleChange} 
                  placeholder="••••••••" 
                  required 
                />
                <button 
                  type="button" 
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
              {strength && (
                <div className="auth-password-strength">
                  <div className="auth-strength-bar">
                    <div style={{ width: strength.width, backgroundColor: strength.color, height: '100%', transition: 'width 0.3s' }} />
                  </div>
                  <span style={{ color: strength.color, fontSize: 12 }}>{strength.label}</span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Confirmer le mot de passe *</label>
              <div className="auth-input-wrapper">
                <FiLock size={16} className="auth-input-icon" />
                <input 
                  type={showConfirm ? "text" : "password"} 
                  name="confirm_password" 
                  className="form-control" 
                  value={form.confirm_password} 
                  onChange={handleChange} 
                  placeholder="••••••••" 
                  required 
                />
                <button 
                  type="button" 
                  className="auth-password-toggle"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                  {showConfirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="auth-checkbox">
                <input 
                  type="checkbox" 
                  checked={acceptTerms} 
                  onChange={(e) => setAcceptTerms(e.target.checked)} 
                />
                <span>J'accepte les <a href="/terms" target="_blank">conditions d'utilisation</a> et la <a href="/privacy" target="_blank">politique de confidentialité</a></span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button 
                type="button" 
                className="auth-btn auth-btn-secondary auth-btn-lg" 
                onClick={handlePreviousStep}
                style={{ flex: 1 }}
              >
                <FiArrowLeft size={18} style={{ marginRight: 8 }} /> Retour
              </button>
              <button 
                type="submit" 
                className="auth-btn auth-btn-primary auth-btn-lg" 
                disabled={loading}
                style={{ flex: 2 }}
              >
                {loading ? 'Création en cours...' : 'Créer mon compte'}
                {!loading && <FiArrowRight size={18} style={{ marginLeft: 8 }} />}
              </button>
            </div>
          </form>
        )}

        {/* Lien vers login */}
        <div className="auth-footer">
          <p>
            Vous avez déjà un compte ?{' '}
            <Link to="/login" className="auth-link">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
